import { ref } from 'vue';
import * as Sentry from '@sentry/vue';
import http from '@/services/http';

/**
 * Resolve location_zone_players ids into a name a person recognises, for the
 * station change schedules that store only `station_change_location_zone_player_id`.
 *
 * A zone player's search document has no display_string of its own. The app
 * names a player by its zone ("{location}-{zone}", as LocationZonePlayerCardHeader
 * does), so that is the name returned here.
 *
 * Same shape as {@link file:src/composables/api/useZoneSummaries.ts}: every id
 * read in one render joins ONE search,
 *
 *   POST /api/location_zone_player/search
 *   { perPage: ids.length, withTotal: false,
 *     filter: { ruleScopes: { id: 'root', scopeType: 'and',
 *       rules: [{ column: 'id', criterion: 'in', value: ids }] } } }
 *
 * and answers are cached module-wide by id. A player the search leaves out is
 * cached as a miss (callers show "player #id"). A failed request caches nothing
 * and is reported. {@link resetPlayerSummaries} empties the cache on logout.
 */

/**
 * - `undefined`: not answered yet.
 * - `null`: the search answered and the player was not in it, or it has no zone name.
 * - a string: the player's name.
 */
export type PlayerNameLookup = string | null | undefined;

interface CacheEntry {
    name: string | null;
    fetchedAt: number;
}

const RESOLVED_REFRESH_MS = 30 * 60_000;
const UNRESOLVED_RETRY_MS = 5 * 60_000;
const BATCH_SIZE = 200;

const cache = ref(new Map<number, CacheEntry>());
const inFlight = new Set<number>();
let queue = new Set<number>();
let scheduled = false;
/** Bumped on reset, so an answer that lands after a logout is dropped. */
let generation = 0;

function positiveId(value: unknown): number | null {
    const id = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

function isFresh(entry: CacheEntry, now: number): boolean {
    const ttl = entry.name === null ? UNRESOLVED_RETRY_MS : RESOLVED_REFRESH_MS;
    return now - entry.fetchedAt < ttl;
}

function nonEmptyString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function playerName(player: Record<string, unknown>): string | null {
    const own = nonEmptyString(player.display_string);
    if (own) return own;
    const zone = player.location_zone;
    if (zone && typeof zone === 'object') {
        return nonEmptyString((zone as { display_string?: unknown }).display_string);
    }
    return null;
}

async function fetchPlayers(ids: number[]): Promise<Map<number, string | null>> {
    const response = await http.post('/api/location_zone_player/search', {
        perPage: ids.length,
        withTotal: false,
        filter: {
            ruleScopes: {
                id: 'root',
                scopeType: 'and',
                rules: [{ id: 'player-summaries', column: 'id', criterion: 'in', value: ids, field: null }],
            },
        },
    });
    const data = (response.data as { data?: Record<string, unknown>[] }).data ?? [];
    const byId = new Map<number, string | null>();
    for (const player of data) {
        const id = positiveId(player.id);
        if (id !== null) byId.set(id, playerName(player));
    }
    return byId;
}

async function flushBatch(ids: number[], batchGeneration: number): Promise<void> {
    try {
        const byId = await fetchPlayers(ids);
        if (batchGeneration !== generation) return;
        const now = Date.now();
        const next = new Map(cache.value);
        for (const id of ids) {
            next.set(id, { name: byId.get(id) ?? null, fetchedAt: now });
        }
        cache.value = next;
    } catch (error) {
        Sentry.captureException(error);
    } finally {
        if (batchGeneration === generation) {
            for (const id of ids) inFlight.delete(id);
        }
    }
}

async function flush(): Promise<void> {
    const ids = [...queue];
    const batchGeneration = generation;
    queue = new Set();
    scheduled = false;
    const batches: Promise<void>[] = [];
    for (let index = 0; index < ids.length; index += BATCH_SIZE) {
        batches.push(flushBatch(ids.slice(index, index + BATCH_SIZE), batchGeneration));
    }
    await Promise.all(batches);
}

function ask(id: number): void {
    const entry = cache.value.get(id);
    if ((entry && isFresh(entry, Date.now())) || inFlight.has(id)) return;
    inFlight.add(id);
    queue.add(id);
    if (!scheduled) {
        scheduled = true;
        void Promise.resolve().then(flush);
    }
}

/** The cached name for one zone player, queuing a lookup when it is missing or stale. */
export function playerNameFor(locationZonePlayerId: unknown): PlayerNameLookup {
    const id = positiveId(locationZonePlayerId);
    if (id === null) return null;
    const entry = cache.value.get(id);
    ask(id);
    return entry ? entry.name : undefined;
}

/** Forget every answer, and drop any lookup still in flight. Called on logout. */
export function resetPlayerSummaries(): void {
    generation += 1;
    cache.value = new Map();
    inFlight.clear();
    queue = new Set();
    scheduled = false;
}
